/**
 * ConnectAccountDialog — focused, single-vendor "Collega account" modal.
 *
 * Mounted by the per-capability detail page **after** the user has picked a
 * vendor via ``AddAccountMenu``. Two single-step flows ship today:
 *
 *   1. ``oauth_redirect``    → one "Autorizza" CTA, click bounces the
 *                              caller to ``window.location = oauth_url``.
 *   2. ``credentials_form``  → schema-driven form (read off ``spec.steps``
 *                              where ``kind === "credentials"``).
 *
 * The vendor is ALWAYS resolved by construction (passed in as ``vendorId``)
 * so the "Vendor mancante per capability X" failure mode that plagued v1's
 * inline overlay is structurally impossible here.
 *
 * State machine (intentionally flat — no XState):
 *
 *   idle ──click Autorizza──▶ starting ──onStartFlow resolves─┐
 *                                                              ├── oauth_redirect → caller navigates
 *                                                              ├── credentials_form → form
 *                                                              └── error → idle + inline banner
 *   form ──submit──▶ submitting ──ok──▶ onOpenChange(false)
 *                              └─err──▶ form + inline banner
 *
 * Pure presentation: every state-changing side effect goes through callback
 * props. No fetch, no router, no store writes — the dialog can be reused in
 * Storybook with a 5-line mock.
 *
 * a11y baseline:
 *   • Radix Dialog (focus trap + Esc).
 *   • Visible ``<label htmlFor>`` per field (no placeholder-only inputs).
 *   • Inline error → ``role="alert" aria-live="polite"``.
 *   • Close button gets an explicit ``aria-label="Chiudi"``.
 */

import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ArrowRight, Loader2 } from "lucide-react";

import * as React from "react";
import { ConnectorDialogShell } from "./ConnectorDialogShell";

import {
  type BaseConnection,
  Button,
  type CapabilityWizardSpec,
  type FieldSpec,
  type WizardStepSpec,
  cn,
  localize,
} from "@ando/ui";

import { InlineErrorBanner } from "./InlineErrorBanner";
import { VendorBrandBadge } from "./VendorBrandBadge";

// ---------------------------------------------------------------------------
// Public contract
// ---------------------------------------------------------------------------

/**
 * Field descriptor the dialog accepts when the BE forwards a credentials
 * schema that the spec doesn't declare statically. White-label PSD2 Open
 * Banking is the driving use case: the wizard has no ``kind:"credentials"``
 * step (consent is OAuth-shaped) but the BE PSD2 bridge returns a
 * ``credentials_form`` with a single ``vat_number`` field — without this
 * the dialog flips to ``phase="form"`` and renders an empty body.
 *
 * Mirrors the BE ``form_schema.fields`` shape (key/label/type/required)
 * so the bridge between :type:`StartFlowResponse` and this dialog stays
 * declarative + DRY across every two-stage credentials flow.
 */
export interface StartFlowField {
  /** Matches the spec's ``CredentialsField.name`` shape — adapter at the
   * call site maps BE-side ``form_schema.fields[].key`` onto this. */
  readonly name: string;
  readonly label: string;
  readonly type: "string" | "secret" | "number" | "boolean";
  readonly required?: boolean;
  readonly placeholder?: string;
  /** Optional helper copy rendered under the input (matches the spec
   * ``CredentialsField.helperText`` so the rendering path is uniform). */
  readonly helperText?: string;
}

export type StartFlowResult =
  | { readonly type: "oauth_redirect"; readonly oauth_url: string }
  | {
      readonly type: "credentials_form";
      /** BE-provided dynamic schema — wins over the spec's static step. */
      readonly fields?: readonly StartFlowField[];
    }
  | { readonly type: "error"; readonly message: string };

export interface ConnectAccountDialogProps {
  /** Dialog open state — controlled. */
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The capability spec — drives title/subtitle/benefits/form fields. */
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  /**
   * Pre-selected vendor (chosen via AddAccountMenu). Empty string when
   * the spec is single-vendor and the picker was skipped — in that case
   * we fall back to ``spec.capability`` for display purposes.
   */
  readonly vendorId: string;
  /**
   * Called when the user clicks "Autorizza". Caller is responsible for
   * starting the upstream flow (OAuth POST, credentials prep) and
   * returns a discriminated result so this dialog can route the
   * redirect inline OR move to the credentials form.
   */
  readonly onStartFlow: (vendorId: string) => Promise<StartFlowResult>;
  /** Called when the user submits the credentials form. */
  readonly onSubmitCredentials?: (
    vendorId: string,
    values: Record<string, string>,
  ) => Promise<void>;
  /**
   * Open the dialog directly on the credentials form, skipping the OAuth
   * "Autorizza" confirm step. The route now starts the flow BEFORE mounting
   * this dialog (OAuth redirects with no modal; only a credentials flow
   * reaches here), so this is the normal path.
   */
  readonly openInForm?: boolean;
  /** BE-provided credentials schema, pre-fetched by the route's start flow. */
  readonly initialFields?: readonly StartFlowField[] | null;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

type DialogPhase = "idle" | "starting" | "form" | "submitting";

/**
 * Locate the credentials step in the spec — required for the
 * ``credentials_form`` branch. Returns ``null`` when the spec is OAuth-only
 * (perfectly valid; the form simply never mounts).
 *
 * Multi-vendor specs may ship one credentials step per vendor by tagging
 * each step with ``vendor: "<id>"`` (e.g. ``database``: postgresql vs
 * sqlite vs odbc all need a different field set). The lookup prefers a
 * vendor-scoped step over an untagged one so the dialog renders the
 * correct fields without per-vendor capability slugs.
 */
export function findCredentialsStep(
  spec: CapabilityWizardSpec<BaseConnection>,
  vendorId: string,
): WizardStepSpec<BaseConnection> | null {
  const credentialsSteps = spec.steps.filter((s) => s.kind === "credentials");
  if (vendorId) {
    const vendorScoped = credentialsSteps.find((s) => s.vendor === vendorId);
    if (vendorScoped) return vendorScoped;
  }
  // Fallback: first step without a vendor tag (the spec's default form).
  return credentialsSteps.find((s) => s.vendor === undefined) ?? null;
}

/**
 * Resolve a human-friendly vendor label from the spec, falling back to the
 * capability title for single-vendor specs that skip the picker entirely.
 */
function resolveVendorLabel(
  spec: CapabilityWizardSpec<BaseConnection>,
  vendorId: string,
  i18n: I18n,
): string {
  if (!vendorId) return localize(spec.title, i18n);
  const match = spec.vendors.find((v) => v.id === vendorId);
  return match ? localize(match.label, i18n) : localize(spec.title, i18n);
}

/** Map a field's type (spec ``CredentialsField`` OR BE ``StartFlowField``
 *  shape — both supported, ``secret`` is the BE marker for password
 *  inputs) → native ``<input type>``. */
function inputTypeFor(field: { readonly type: string }): React.HTMLInputTypeAttribute {
  switch (field.type) {
    case "password":
    case "secret":
      return "password";
    case "email":
      return "email";
    case "url":
      return "url";
    case "number":
      return "number";
    default:
      return "text";
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ConnectAccountDialog: React.FC<ConnectAccountDialogProps> = ({
  open,
  onOpenChange,
  spec,
  vendorId,
  onStartFlow,
  onSubmitCredentials,
  openInForm = false,
  initialFields = null,
}) => {
  const { i18n } = useLingui();
  const [phase, setPhase] = React.useState<DialogPhase>("idle");
  const [error, setError] = React.useState<string | null>(null);
  const [formValues, setFormValues] = React.useState<Record<string, string>>({});
  // BE-provided field schema retained between ``handleStart`` and the
  // form render so a spec without a ``kind:"credentials"`` step (e.g.
  // openBankingSpec, where consent is OAuth-shaped) can still show the
  // PSD2 VAT input the BE asked for. Cleared on close so a stale
  // schema doesn't leak between vendor switches.
  const [dynamicFields, setDynamicFields] = React.useState<readonly StartFlowField[] | null>(null);

  // Reset internal state every time the dialog opens — without this, a user
  // who hit an error, closed, and reopened would see the stale banner.
  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setFormValues({});
    // The route starts the flow before mounting this dialog: OAuth already
    // redirected (dialog never opens for it), so a mounted dialog is ALWAYS a
    // credentials form. ``openInForm`` skips the obsolete "Autorizza" step.
    if (openInForm) {
      setDynamicFields(initialFields ?? null);
      setPhase("form");
    } else {
      setDynamicFields(null);
      setPhase("idle");
    }
  }, [open, openInForm, initialFields]);

  const credentialsStep = React.useMemo(
    () => findCredentialsStep(spec, vendorId),
    [spec, vendorId],
  );
  const vendorLabel = resolveVendorLabel(spec, vendorId, i18n);
  // ``vendorId`` may be the empty string for single-vendor specs that skip
  // the picker — fall back to capability slug so the brand badge still
  // renders a recognisable mark (e.g. ``aruba_pec`` → Aruba icon).
  const iconKey = vendorId || spec.capability;

  // Benefits — clamp to 3 to keep the dialog tight; spec may declare more
  // for the empty-state grid but the connect modal is a confirmation
  // surface, not a marketing one.
  const benefits = spec.benefits.slice(0, 3);

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleStart = React.useCallback(async () => {
    setError(null);
    setPhase("starting");
    try {
      const result = await onStartFlow(vendorId);
      if (result.type === "oauth_redirect") {
        // Caller is responsible for ``window.location =`` — we stay in
        // ``starting`` so the spinner remains visible until the page
        // navigates and the dialog is unmounted externally.
        return;
      }
      if (result.type === "credentials_form") {
        // Remember a BE-supplied schema so the form below can render
        // it when the spec doesn't declare a ``kind:"credentials"`` step.
        setDynamicFields(result.fields ?? null);
        setPhase("form");
        return;
      }
      // error branch
      setError(result.message);
      setPhase("idle");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : i18n._(msg`Avvio del flusso non riuscito.`);
      setError(message);
      setPhase("idle");
    }
  }, [onStartFlow, vendorId]);

  const handleSubmitForm = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // We accept either a spec-declared credentials step OR a BE-supplied
      // ``dynamicFields`` schema (PSD2 OB and other two-stage flows that
      // don't carry a static credentials step in the wizard spec).
      if (!onSubmitCredentials) return;
      if (!credentialsStep && (!dynamicFields || dynamicFields.length === 0)) {
        return;
      }
      setError(null);
      setPhase("submitting");
      try {
        await onSubmitCredentials(vendorId, formValues);
        onOpenChange(false);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : i18n._(msg`Invio delle credenziali non riuscito.`);
        setError(message);
        setPhase("form");
      }
    },
    [credentialsStep, formValues, onOpenChange, onSubmitCredentials, vendorId],
  );

  const handleFieldChange = React.useCallback((name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCancel = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Disable form submission while a field marked ``required`` is empty —
  // matches v1 ergonomics where the CTA went disabled instead of erroring
  // server-side.
  // Effective field list — BE-supplied schema (``dynamicFields``) wins so
  // a spec without a static credentials step (e.g. ``openBankingSpec``)
  // can still render the BE-prompted inputs. Falls back to the spec when
  // the BE doesn't ship a schema (legacy single-stage credentials specs).
  //
  // Spec-side ``FieldSpec`` types ``label``/``placeholder``/``helperText``
  // as ``LocalizedText`` (``string | MessageDescriptor``); BE-side
  // ``StartFlowField`` ships pre-resolved strings. The render path
  // (``<label>{field.label}</label>``) crashes when React receives a
  // raw ``MessageDescriptor`` ("Objects are not valid as a React child").
  // Normalise here so the form layer downstream sees uniform strings —
  // ``localize`` is a no-op on already-resolved strings.
  const effectiveFields = React.useMemo<readonly StartFlowField[]>(() => {
    if (dynamicFields && dynamicFields.length > 0) {
      return dynamicFields;
    }
    const specFields = credentialsStep?.fields ?? [];
    return specFields.map((f): StartFlowField => {
      // Field types narrower than ``StartFlowField`` (``email``/``url``/
      // ``select``/``tenant_code``/``callback_readonly``) fall back to
      // ``"string"`` for the dialog's purposes — ``inputTypeFor`` covers
      // the input-type mapping separately. ``password`` is renamed to
      // ``secret`` for parity with the BE shape (both routes converge on
      // ``inputTypeFor`` which already accepts both spellings).
      const resolvedType: StartFlowField["type"] =
        f.type === "password"
          ? "secret"
          : f.type === "number" || f.type === "boolean"
            ? f.type
            : "string";
      return {
        name: f.name,
        label: localize(f.label, i18n),
        type: resolvedType,
        required: f.required,
        placeholder: f.placeholder == null ? undefined : localize(f.placeholder, i18n),
        helperText: f.helperText == null ? undefined : localize(f.helperText, i18n),
      };
    });
  }, [dynamicFields, credentialsStep, i18n]);

  const requiredMissing = React.useMemo(() => {
    if (effectiveFields.length === 0) return false;
    return effectiveFields.some((f) => f.required === true && !(formValues[f.name] ?? "").trim());
  }, [effectiveFields, formValues]);

  // ── Inline pieces ────────────────────────────────────────────────────

  const titleId = React.useId();

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <ConnectorDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={i18n._(msg`Collega ${vendorLabel}`)}
      size="sm"
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="min-w-0 pr-8">
        <h2
          id={titleId}
          className="font-display font-semibold text-[15px] text-ink-1 leading-tight"
        >
          <Trans>Collega {vendorLabel}</Trans>
        </h2>
        <p className="mt-1 text-[12px] text-ink-2 leading-snug">{localize(spec.subtitle, i18n)}</p>
      </div>

      {/* ── Body ────────────────────────────────────────────────── */}
      <div className="mt-4 flex items-start gap-3">
        <VendorBrandBadge vendorId={iconKey} fallbackIcon={spec.icon} size="lg" />
        <p className="text-[12.5px] text-ink-2 leading-relaxed">
          <Trans>
            Connetterai questo account a Ando. La conservazione delle credenziali avviene cifrata;
            revoca disponibile in qualsiasi momento dalla pagina Integrazioni.
          </Trans>
        </p>
      </div>

      {/* Benefit bullets — only on the (now-rare) confirm step; redundant on
          the form, and the capability page already lists them. */}
      {phase === "idle" && benefits.length > 0 ? (
        <ul className="mt-4 space-y-1.5 rounded-md border border-hairline bg-white/40 px-3 py-2.5">
          {benefits.map((b) => {
            const title = localize(b.title, i18n);
            const body = localize(b.body, i18n);
            return (
              <li
                key={title}
                className="flex items-start gap-2 text-[12px] text-ink-2 leading-snug"
              >
                <span className="mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-ando-orange">
                  {b.icon}
                </span>
                <span className="min-w-0">
                  <span className="font-medium text-ink-1">{title}</span>
                  {body ? <span className="text-ink-3"> · {body}</span> : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* ── Credentials form (only when we're past the start flow) ─ */}
      {phase === "form" || phase === "submitting" ? (
        <form
          onSubmit={handleSubmitForm}
          className="mt-4 space-y-3"
          aria-busy={phase === "submitting"}
        >
          {effectiveFields.map((field) => {
            const fieldId = `cad-${field.name}`;
            return (
              <div key={field.name} className="flex flex-col gap-1">
                <label htmlFor={fieldId} className="font-medium text-[11.5px] text-ink-2">
                  {field.label}
                  {field.required === true ? (
                    <span className="ml-0.5 text-error" aria-hidden>
                      *
                    </span>
                  ) : null}
                </label>
                <input
                  id={fieldId}
                  name={field.name}
                  type={inputTypeFor(field)}
                  required={field.required === true}
                  placeholder={field.placeholder}
                  // May 24, 2026 — credentials forms collect API
                  // tokens / app passwords / service credentials,
                  // NOT the user's account password. Setting
                  // ``current-password`` would invite the browser
                  // password manager to autofill the user's Gmail
                  // login into a Salesforce API token field
                  // (data integrity + privacy hazard). ``off``
                  // for all fields; ``new-password`` on password
                  // inputs to explicitly suppress autofill UI.
                  // ``secret`` is the schema-side marker for password-shaped
                  // inputs (the BE form_schema + the spec CredentialsField
                  // both use it). ``new-password`` discourages the browser
                  // password manager from auto-filling the user's account
                  // password into an API token field.
                  autoComplete={field.type === "secret" ? "new-password" : "off"}
                  data-1p-ignore={field.type === "secret" ? "true" : undefined}
                  value={formValues[field.name] ?? ""}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  disabled={phase === "submitting"}
                  className={cn(
                    "h-9 rounded-md border border-hairline bg-white/80 px-2.5 text-[13px] text-ink-1 placeholder:text-ink-3",
                    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                    "disabled:opacity-60",
                  )}
                />
                {field.helperText ? (
                  <span className="text-[11px] text-ink-3">{field.helperText}</span>
                ) : null}
              </div>
            );
          })}

          <InlineErrorBanner message={error} />

          <div className="mt-1 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={handleCancel}
              disabled={phase === "submitting"}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={phase === "submitting" || requiredMissing}
            >
              {phase === "submitting" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  <Trans>Invio…</Trans>
                </>
              ) : (
                <>
                  <Trans>Collega</Trans>
                  <ArrowRight aria-hidden />
                </>
              )}
            </Button>
          </div>
        </form>
      ) : (
        /* ── OAuth-style footer (idle / starting) ─────────────── */
        <>
          <InlineErrorBanner message={error} />
          <div className="mt-5 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={handleCancel}
              disabled={phase === "starting"}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={handleStart}
              disabled={phase === "starting"}
              aria-label={i18n._(msg`Autorizza ${vendorLabel}`)}
            >
              {phase === "starting" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  <Trans>Avvio…</Trans>
                </>
              ) : (
                <>
                  <Trans>Autorizza</Trans>
                  <ArrowRight aria-hidden />
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </ConnectorDialogShell>
  );
};
